(function () {
    'use strict';

    angular
        .module('portalApp')
        .factory('OgrChart', OgrChart);

    OgrChart.$inject = ['$window', '$document', 'chartConstants'];

    /**
     * OgrChart: OGResearch's SVG line chart renderer.
     *
     * The renderer draws a chart spec:
     *   height         chart height in px (default 250)
     *   title          {text, show, font: {size, fontFamily, fontWeight, color}}  -- centred above the plot
     *   xAxis          {show, font, grid, scale: 'category'|'linear', min, max, step,
     *                   labelOverlap: 'hide' (default: overlapping labels are left out) | 'wrap' (moved to a
     *                   further row below),
     *                   shading: [{from, to, color, opacity, onTop}]   -- shaded x ranges (category indexes or
     *                   values), behind the series or, with onTop, over them
     *                   formatLabel: function ({text, value, step}) -> label text}
     *   yAxis          {show, font, grid, min, max, step, title: {show, text, font}}
     *   series         [{kind: 'line'|'band', label, color, lineWidth, dash ([dash, gap] or 'dash,gap'),
     *                    marker: {show, size}, hidden, edgeColor, tooltipFont,
     *                    spline: true -> a line is drawn as a natural cubic spline through its points,
     *                    points: [{x, y, text}] for lines, [{x, upper, lower}] for bands,
     *                    axisOnly: true -> the series only supplies x categories (never drawn or tracked)}]
     *   seriesStyle    {defaultOpacity: of every series when not hovered (default 1),
     *                   hoverOpacity: of the series under the pointer (unset: no hover effect)}
     *   legend         {show, font, clickToHide (default true: clicking an item hides/shows its series)}
     *                  -- a line series is shown as a ring on a line, or just the line when it has no markers
     *   tracker        true: vertical tracker line with a tooltip per series follows the pointer
     *   tooltipText    function (point, defaultText) -> tooltip text ('<br/>' separates lines)
     *   placeholder    {show, text, fontSize, opacity}  -- text centred over the plot in place of a chart, e.g. "Empty"
     *   animate        true: the lines are drawn in from the left and their markers pop up one by one
     *
     * A chart without any points keeps a plain grid (axes hidden) or its bare axes (axes shown).
     */
    function OgrChart($window, $document, chartConstants) {
        var SVG_NS = 'http://www.w3.org/2000/svg';
        var measureNode = null; // hidden DOM element used for tooltip text measurement
        var measureCache = {};  // tooltip text sizes by font + text (measuring forces a browser layout)
        var measureCacheSize = 0;

        var TRACKER = {
            guideColor: 'Black',     // vertical line under the pointer
            guideWidth: 1,
            markerSize: 10,          // marker drawn on each tracked point
            markerRingWidth: 3,
            markerFill: 'white',
            font: {size: '12px', fontFamily: 'Segoe UI', fontStyle: 'normal', fontWeight: 'normal'}, // tooltip font
            textColor: '#ffffff',
            textMargin: 4,           // tooltip text to box edge
            pointGap: 16,            // point to the near box edge
            stackGap: 8,             // between stacked boxes
            clearanceTop: 3,         // stacked boxes keep this far from the plot top
            clearanceBottom: 4,      // ... and from the plot bottom
            sideRoom: 2,             // a box goes left of its point before the right side gets this many boxes tight
            boxOpacity: 1,
            boxRadius: 3,
            arrowSize: 5
        };

        var STYLE = {
            gridColor: '#DFDFDF',
            axisColor: '#8E8E8E',
            areaBorderColor: 'Gray',
            areaBorderWidth: 0.5,
            xTickSize: 5,
            yTickSize: 6,
            defaultFont: {size: '12px', fontFamily: 'Helvetica', color: '#1e2834'},
            titleFont: {size: '16px', fontFamily: 'Helvetica', color: '#1e2834'},
            padding: 20,
            titleGap: 10,                // between the title and the plot
            titleBaselineShift: 5,       // title baseline below the middle of its line
            yAxisBand: 26,               // left of the plot besides the Y labels: padding, gap, ticks and axis line
            yTitleBand: 37,              // extra room for a Y axis title
            xAxisBand: 26,               // below the plot besides the X labels: axis line, ticks, gap, padding
            xLabelOffset: 10.5,          // plot bottom to the top of the first X label row
            legendFrame: 2,              // added to the legend height below the plot
            emptyMargin: 26,
            labelGap: 2,                 // least space between two X labels, and to the chart's right edge
            // labelOverlap 'wrap': label rows kept below the plot by chart width (the first entry the width reaches),
            // so charts side by side keep the same plot height
            wrapRows: [{minWidth: 400, rows: 1}, {minWidth: 230, rows: 2}, {minWidth: 0, rows: 3}],
            emptyGrid: {columns: 7, rows: 6},  // grid of a chart without points and axes
            emptyAxisSteps: 6,           // ticks on the bare X axis of a chart without points
            // animate
            drawInMs: 2000,              // lines are drawn in from the left over this time
            markerPopMs: 200,            // each marker grows to full size in this time
            // legend geometry
            legendItemSize: 9,
            legendSymbolRing: 3,         // line series symbol: ring radius
            legendSymbolReach: 7,        // line series symbol: half of its total width
            legendSymbolStroke: 2,       // line series symbol: stroke width
            legendItemPadding: 10,
            legendElementSpacing: 10,
            legendSideMargin: 21,        // free space left and right of the widest legend row
            legendBottomGap: 16,         // gap between the legend box and the svg bottom
            xIntervalPx: 62.5,           // aimed-for pixels between two X axis ticks
            minTickGapPx: 5,             // a configured step giving ticks closer than this is ignored
            yIntervalPx: 100 / 3,        // aimed-for pixels between two Y axis ticks
            // colours for series that do not set their own
            fallbackColors: chartConstants.lineColorSet
        };

        var service = {
            render: render,
            destroy: destroy
        };

        return service;

        //************************************************************************************
        // public API
        //************************************************************************************

        /**
         * Removes a chart previously rendered into the element (if any).
         */
        function destroy(element) {
            var el = element[0] || element;
            var state = el.__ogrChart;
            if (state) {
                if (state.stopTracker) {
                    state.stopTracker();
                }
                if (state.stopAnimation) {
                    state.stopAnimation();
                }
                if (state.svg && state.svg.parentNode) {
                    state.svg.parentNode.removeChild(state.svg);
                }
                if (state.overlay && state.overlay.parentNode) {
                    state.overlay.parentNode.removeChild(state.overlay);
                }
                delete el.__ogrChart;
            }
        }

        /**
         * Renders the chart described by spec into the element.
         */
        function render(element, spec) {
            var el = element[0] || element;
            destroy(element);

            var width = el.clientWidth || angular.element(el).width() || 0;
            var height = parseFloat(spec.height) || 250;
            if (!(width > 0) || !(height > 0)) {
                return;
            }

            var model = buildModel(spec, width, height);
            var svg = createEl('svg', {width: width, height: height});
            svg.style.width = width + 'px';
            svg.style.height = height + 'px';
            svg.style.display = 'block';
            svg.style.margin = '0 auto';
            svg.setAttribute('class', 'ogr-chart');

            drawArea(svg, model);
            drawTitle(svg, model);
            drawGrid(svg, model);
            drawClip(svg, model);
            drawShading(svg, model, false);
            drawSeries(svg, model);
            drawAxes(svg, model);
            drawShading(svg, model, true);
            drawLegend(svg, model);

            el.style.position = el.style.position || 'relative';
            el.appendChild(svg);

            var overlay = drawPlaceholder(el, model);

            el.__ogrChart = {svg: svg, overlay: overlay, model: model};
            bindLegend(el, model, spec);
            if (model.hasData && model.tracker) {
                el.__ogrChart.stopTracker = bindTracker(el, svg, model);
            }
            if (model.hasData && model.animate) {
                el.__ogrChart.stopAnimation = drawIn(model);
            }
        }

        /* legend.clickToHide (default true): clicking an item flips that series' hidden flag and redraws the
           whole chart; hidden series keep their legend slot, drawn in gray */
        function bindLegend(el, model, spec) {
            if (!model.legendItems || model.legend.clickToHide === false) {
                return;
            }
            angular.forEach(model.legendItems, function (item) {
                if (item.series.axisOnly) {
                    return;
                }
                item.node.addEventListener('click', function () {
                    var raw = spec.series[item.series.index];
                    raw.hidden = !raw.hidden;
                    render(el, spec);
                });
            });
        }

        //************************************************************************************
        // model
        //************************************************************************************

        function buildModel(spec, width, height) {
            var xAxis = spec.xAxis || {};
            var yAxis = spec.yAxis || {};
            var legend = spec.legend || {};
            var title = spec.title || {};

            var seriesStyle = spec.seriesStyle || {};
            var series = normalizeSeries(spec.series || [], angular.isDefined(seriesStyle.defaultOpacity) ? seriesStyle.defaultOpacity : 1);
            var hasData = series.some(function (s) {
                return s.points.length > 0;
            });

            var model = {
                width: width,
                height: height,
                series: series,
                hasData: hasData,
                axesVisible: hasData && xAxis.show !== false && yAxis.show !== false,
                // series without any points: the axes stay, without labels
                bareAxes: !hasData && series.length > 0 && xAxis.show !== false && yAxis.show !== false,
                xAxis: xAxis,
                yAxis: yAxis,
                xFont: fontOf(xAxis.font),
                yFont: fontOf(yAxis.font),
                titleText: title.show !== false && title.text ? String(title.text) : '',
                titleFont: fontOf(title.font, STYLE.titleFont),
                labelOverlap: xAxis.labelOverlap === 'wrap' ? 'wrap' : 'hide',
                animate: spec.animate === true,
                legend: legend,
                legendVisible: hasData && legend.show === true,
                legendFont: fontOf(legend.font),
                placeholder: spec.placeholder || {},
                tracker: spec.tracker === true,
                hoverOpacity: seriesStyle.hoverOpacity,
                tooltipText: spec.tooltipText,
                isCategory: xAxis.scale === 'category'
            };

            computeXScale(model);
            computeYScale(model);
            computeLayout(model);
            return model;
        }

        function normalizeSeries(rawSeries, opacity) {
            var result = [];
            for (var i = 0; i < rawSeries.length; i++) {
                var s = rawSeries[i];
                if (!s || !angular.isArray(s.points)) {
                    continue;
                }
                var kind = s.kind === 'band' ? 'band' : 'line';
                var points = s.points.map(function (p) {
                    return (kind === 'band')
                        ? {x: p.x, upper: toNumberOrNull(p.upper), lower: toNumberOrNull(p.lower), text: p.text}
                        : {x: p.x, y: toNumberOrNull(p.y), text: p.text};
                });
                result.push({
                    index: i,
                    kind: kind,
                    label: angular.isDefined(s.label) ? String(s.label) : '',
                    color: s.color || STYLE.fallbackColors[i % STYLE.fallbackColors.length],
                    axisOnly: s.axisOnly === true,
                    hidden: s.hidden === true,
                    lineWidth: angular.isDefined(s.lineWidth) ? s.lineWidth : 2,
                    dash: s.dash,
                    spline: s.spline === true,
                    opacity: opacity,
                    marker: s.marker || {},
                    edgeColor: s.edgeColor,
                    tooltipFont: s.tooltipFont || {},
                    points: points
                });
            }
            return result;
        }

        function computeXScale(model) {
            var xAxis = model.xAxis;
            var i, j, s;
            if (model.isCategory) {
                // categories in order of first appearance across the series
                var categories = [];
                var index = {};
                for (i = 0; i < model.series.length; i++) {
                    s = model.series[i];
                    for (j = 0; j < s.points.length; j++) {
                        var key = String(s.points[j].x);
                        if (!index.hasOwnProperty(key)) {
                            index[key] = categories.length;
                            categories.push(key);
                        }
                        s.points[j].xIndex = index[key];
                    }
                }
                model.categories = categories;
                var gaps = Math.max(categories.length - 1, 0);
                // the label spacing is judged on the chart width inside the padding
                var labelSpan = model.width - 2 * STYLE.padding;
                var interval = usableStep(Math.ceil(xAxis.step), gaps, model.width) || categoryInterval(gaps, labelSpan);
                var ticks = [];
                for (i = 0; i < categories.length; i += interval) {
                    ticks.push({value: i, text: categories[i]});
                }
                model.xTicks = ticks;
                model.xMin = 0;
                model.xMax = Math.max(categories.length - 1, 0);
            } else {
                var dataMin = Number.POSITIVE_INFINITY, dataMax = Number.NEGATIVE_INFINITY;
                for (i = 0; i < model.series.length; i++) {
                    s = model.series[i];
                    for (j = 0; j < s.points.length; j++) {
                        var xv = parseFloat(s.points[j].x);
                        if (!isNaN(xv)) {
                            dataMin = Math.min(dataMin, xv);
                            dataMax = Math.max(dataMax, xv);
                        }
                    }
                }
                if (!isFinite(dataMin)) {
                    dataMin = 0;
                    dataMax = 1;
                }
                var min = finiteOr(xAxis.min, dataMin);
                var max = finiteOr(xAxis.max, dataMax);
                if (max <= min) {
                    max = min + 1;
                }
                var step = usableStep(xAxis.step, max - min, model.width) || numericNiceInterval(max - min, model.width, true);
                var xTicks = [];
                for (var v = min, k = 0; v <= max + step * 1e-6; k++, v = min + k * step) {
                    xTicks.push({value: v, text: formatNumber(v)});
                }
                model.xMin = min;
                model.xMax = max;
                model.xInterval = step;
                model.xTicks = xTicks;
            }
            // X labels can be reformatted by the spec
            if (angular.isFunction(xAxis.formatLabel)) {
                angular.forEach(model.xTicks, function (tick) {
                    tick.text = String(xAxis.formatLabel({text: tick.text, value: tick.value, step: model.xInterval}));
                });
            }
        }

        function computeYScale(model) {
            var yAxis = model.yAxis;
            var dataMin = Number.POSITIVE_INFINITY, dataMax = Number.NEGATIVE_INFINITY;
            for (var i = 0; i < model.series.length; i++) {
                var s = model.series[i];
                if (s.axisOnly || s.hidden) {
                    continue;
                }
                for (var j = 0; j < s.points.length; j++) {
                    var p = s.points[j];
                    var vals = (s.kind === 'band') ? [p.upper, p.lower] : [p.y];
                    for (var k = 0; k < vals.length; k++) {
                        if (vals[k] !== null && !isNaN(vals[k])) {
                            dataMin = Math.min(dataMin, vals[k]);
                            dataMax = Math.max(dataMax, vals[k]);
                        }
                    }
                }
            }
            if (!isFinite(dataMin)) {
                dataMin = 0;
                dataMax = 1;
            }
            var min = finiteOr(yAxis.min, dataMin);
            var max = finiteOr(yAxis.max, dataMax);
            if (max <= min) {
                max = min + 1;
                min = min - 1;
            }
            model.yMin = min;
            model.yMax = max;
        }

        function computeLayout(model) {
            var pad = STYLE.padding;
            model.titleSize = model.titleText ? measureDom(model.titleText, model.titleFont) : null;
            var top = pad + (model.titleSize ? model.titleSize.height + STYLE.titleGap : 0);
            var left, right = pad, bottom;
            var yTitleVisible = model.axesVisible && model.yAxis.title && model.yAxis.title.show !== false
                && model.yAxis.title.text;
            model.legendBox = model.legendVisible ? layoutLegend(model) : null;
            var legendBand = model.legendBox ? model.legendBox.height + STYLE.legendFrame : 0;
            model.legendBand = legendBand;
            model.yTicks = [];

            if (!model.axesVisible) {
                left = STYLE.emptyMargin;
                bottom = STYLE.emptyMargin + legendBand;
            } else {
                // Y step from the tallest the plot can be (from its top down to the padding)
                var plotHeight = model.height - top - pad - legendBand;
                var interval = usableStep(model.yAxis.step, model.yMax - model.yMin, plotHeight)
                    || numericNiceInterval(model.yMax - model.yMin, plotHeight, false);
                // ticks start at the axis minimum, one step apart; each value is rounded to the step's decimals
                // (at least one) so float noise never reaches a label
                var decimals = Math.max(1, decimalsOf(interval));
                var stepCount = Math.floor((model.yMax - model.yMin) / interval + 1e-9);
                for (var k = 0; k <= stepCount; k++) {
                    var v = parseFloat((model.yMin + k * interval).toFixed(decimals));
                    if (v >= model.yMin && v <= model.yMax) {
                        model.yTicks.push({value: v, text: formatNumber(v)});
                    }
                }
                model.yInterval = interval;
                var labelWidth = 0;
                angular.forEach(model.yTicks, function (tick) {
                    labelWidth = Math.max(labelWidth, measureDom(tick.text, model.yFont).width);
                });
                left = STYLE.yAxisBand + labelWidth + (yTitleVisible ? STYLE.yTitleBand : 0);

                // X labels: their rows are known once the plot's horizontal extent is
                var rowHeight = 0;
                angular.forEach(model.xTicks, function (tick) {
                    tick.size = measureDom(tick.text, model.xFont);
                    rowHeight = Math.max(rowHeight, tick.size.height);
                });
                model.xRowHeight = rowHeight;
                var plotWidth = model.width - right - left;
                var rowsUsed = arrangeXLabels(model, left, plotWidth);
                model.xRows = model.labelOverlap === 'wrap' ? Math.max(rowsUsed, wrapRowsFor(model.width)) : 1;
                bottom = STYLE.xAxisBand + model.xRows * rowHeight + legendBand;
            }

            model.plot = {
                left: left,
                top: top,
                right: model.width - right,
                bottom: model.height - bottom
            };
            model.plot.width = model.plot.right - model.plot.left;
            model.plot.height = model.plot.bottom - model.plot.top;
            model.yTitleVisible = yTitleVisible;
            if (model.axesVisible) {
                placeXLabels(model);
            }
        }

        /* X labels, centred under their ticks (on the whole pixel left of the tick) and kept inside the chart,
           must stay labelGap apart: 'hide' leaves out a label that would come closer to the previous shown one,
           'wrap' puts it into the first row where it keeps that gap. Returns the number of label rows. */
        function arrangeXLabels(model, plotLeft, plotWidth) {
            var span = model.xMax - model.xMin;
            var rowEnds = [];
            angular.forEach(model.xTicks, function (tick) {
                var ratio = span > 0 ? (tick.value - model.xMin) / span : 0.5;
                var width = tick.size.width;
                var x = Math.min(Math.floor(plotLeft + ratio * plotWidth) - width / 2, model.width - STYLE.labelGap - width);
                var row = 0;
                if (model.labelOverlap === 'wrap') {
                    while (row < rowEnds.length && rowEnds[row] + STYLE.labelGap > x) {
                        row++;
                    }
                } else if (rowEnds.length > 0 && rowEnds[0] + STYLE.labelGap > x) {
                    tick.hidden = true;
                }
                tick.row = row;
                tick.labelX = x;
                if (!tick.hidden && tick.text !== '') {
                    rowEnds[row] = x + width;
                }
            });
            return Math.max(rowEnds.length, 1);
        }

        function wrapRowsFor(chartWidth) {
            for (var i = 0; i < STYLE.wrapRows.length; i++) {
                if (chartWidth >= STYLE.wrapRows[i].minWidth) {
                    return STYLE.wrapRows[i].rows;
                }
            }
            return 1;
        }

        /* X label rows below the plot */
        function placeXLabels(model) {
            var firstRow = Math.floor(model.plot.bottom) + STYLE.xLabelOffset;
            angular.forEach(model.xTicks, function (tick) {
                tick.label = {x: tick.labelX, y: firstRow + (tick.row + 0.5) * model.xRowHeight, anchor: 'start'};
            });
        }

        //************************************************************************************
        // scales & helpers
        //************************************************************************************

        function xToPx(model, value) {
            var span = model.xMax - model.xMin;
            var ratio = span > 0 ? (value - model.xMin) / span : 0.5;
            return model.plot.left + ratio * model.plot.width;
        }

        function yToPx(model, value) {
            var span = model.yMax - model.yMin;
            var ratio = span > 0 ? (value - model.yMin) / span : 0.5;
            return model.plot.bottom - ratio * model.plot.height;
        }

        function pointX(model, point) {
            return xToPx(model, model.isCategory ? point.xIndex : parseFloat(point.x));
        }

        /* 1px lines are drawn on pixel centres: vertical ones on the pixel at or right of the position,
           horizontal ones on the pixel at or above it */
        function crispUp(v) {
            return Math.ceil(v) + 0.5;
        }

        function crispDown(v) {
            return Math.floor(v) + 0.5;
        }

        /* how many intervals fit on an axis of this pixel length (at least one) */
        function intervalBudget(axisLength, horizontal) {
            return Math.max(axisLength / (horizontal ? STYLE.xIntervalPx : STYLE.yIntervalPx), 1);
        }

        /* tick step for a numeric axis: the smallest of 1, 2, 5, 10 x 10^n that keeps the number of intervals
           within the budget */
        function numericNiceInterval(span, axisLength, horizontal) {
            if (!(span > 0)) {
                return 1;
            }
            var budget = intervalBudget(axisLength, horizontal);
            var magnitude = Math.pow(10, Math.floor(Math.log(span / budget) / Math.LN10));
            var steps = [magnitude, 2 * magnitude, 5 * magnitude, 10 * magnitude];
            var pick = 0;
            while (pick < steps.length - 1 && span / steps[pick] > budget) {
                pick++;
            }
            return steps[pick];
        }

        /* a configured tick step, or null when it cannot be used: not a positive finite number, or so small
           that the ticks would be closer than minTickGapPx on an axis of axisLength pixels */
        function usableStep(step, span, axisLength) {
            if (!isFiniteNumber(step) || step <= 0) {
                return null;
            }
            return (span / step > axisLength / STYLE.minTickGapPx) ? null : step;
        }

        function isFiniteNumber(v) {
            return angular.isNumber(v) && isFinite(v);
        }

        /* a configured axis limit, or the fallback when it is not a finite number */
        function finiteOr(value, fallback) {
            return isFiniteNumber(value) ? value : fallback;
        }

        /* category axis: label every n-th category so the labels fit the budget */
        function categoryInterval(categoryGaps, axisLength) {
            return Math.max(1, Math.floor(categoryGaps / intervalBudget(axisLength, true)));
        }

        /* number -> label text without floating point noise */
        function formatNumber(v) {
            return String(parseFloat(Number(v).toFixed(10)));
        }

        /* number of decimal places needed to write the value exactly (max 10) */
        function decimalsOf(v) {
            var text = formatNumber(v);
            var dot = text.indexOf('.');
            return dot < 0 ? 0 : text.length - dot - 1;
        }

        function toNumberOrNull(v) {
            if (v === null || angular.isUndefined(v)) {
                return null;
            }
            var n = parseFloat(v);
            return isNaN(n) ? null : n;
        }

        function fontOf(font, defaults) {
            font = font || {};
            defaults = defaults || STYLE.defaultFont;
            return {
                size: font.size || defaults.size,
                fontFamily: font.fontFamily || defaults.fontFamily,
                fontWeight: font.fontWeight,
                color: font.color || defaults.color
            };
        }

        function createEl(tag, attrs) {
            var node = $document[0].createElementNS(SVG_NS, tag);
            setAttrs(node, attrs);
            return node;
        }

        function setAttrs(node, attrs) {
            if (attrs) {
                for (var key in attrs) {
                    if (attrs.hasOwnProperty(key) && angular.isDefined(attrs[key]) && attrs[key] !== null) {
                        node.setAttribute(key, attrs[key]);
                    }
                }
            }
            return node;
        }

        function textEl(text, attrs, font) {
            var node = createEl('text', attrs);
            node.setAttribute('fill', font.color);
            node.setAttribute('font-size', font.size);
            node.setAttribute('font-family', font.fontFamily);
            node.setAttribute('font-weight', font.fontWeight || 'normal');
            node.textContent = text;
            return node;
        }

        /* series.dash: [dash, gap] or 'dash,gap' */
        function seriesDash(s) {
            if (!s.dash) {
                return null;
            }
            var parts = String(s.dash).split(',').map(function (p) {
                return parseFloat(p);
            });
            // "1,0" means a solid line
            if (parts.length < 2 || parts[1] === 0) {
                return null;
            }
            return parts.join(',');
        }

        //************************************************************************************
        // drawing
        //************************************************************************************

        /* xAxis.shading: shaded x ranges, behind the series or (onTop) over them, clipped to the plot */
        function drawShading(svg, model, onTop) {
            var shades = (model.xAxis.shading || []).filter(function (shade) {
                return shade && !!shade.onTop === onTop;
            });
            if (!model.hasData || shades.length === 0) {
                return;
            }
            var g = createEl('g', {'class': 'ogr-chart-shading', 'clip-path': 'url(#' + model.clipId + ')'});
            for (var i = 0; i < shades.length; i++) {
                var shade = shades[i];
                var from = model.isCategory ? shade.from : parseFloat(shade.from);
                var to = model.isCategory ? shade.to : parseFloat(shade.to);
                if (!angular.isNumber(from) || !angular.isNumber(to) || from < 0 || to < 0) {
                    continue;
                }
                var x1 = xToPx(model, Math.min(from, to));
                var x2 = xToPx(model, Math.max(from, to));
                g.appendChild(createEl('rect', {
                    x: x1, y: model.plot.top, width: Math.max(x2 - x1, 0), height: model.plot.height,
                    fill: shade.color || '#aea', 'fill-opacity': angular.isDefined(shade.opacity) ? shade.opacity : 0.25,
                    stroke: 'none'
                }));
            }
            svg.appendChild(g);
        }

        /* X positions of the vertical grid lines / X ticks and Y positions of the horizontal grid lines / Y ticks:
           those of the axis ticks, or for a chart without points the bare axis steps or the plain grid */
        function gridLines(model) {
            var plot = model.plot;
            var i;
            var lines = {x: [], y: []};
            if (model.axesVisible) {
                lines.x = model.xTicks.map(function (tick) {
                    return crispUp(xToPx(model, tick.value));
                });
                lines.y = model.yTicks.map(function (tick) {
                    return crispDown(yToPx(model, tick.value));
                });
            } else if (model.bareAxes) {
                for (i = 0; i <= STYLE.emptyAxisSteps; i++) {
                    lines.x.push(crispUp(plot.left + i / STYLE.emptyAxisSteps * plot.width));
                }
            } else if (!model.hasData) {
                // columns: a line through the middle of each; rows: a line between each two
                var grid = STYLE.emptyGrid;
                for (i = 0; i < grid.columns; i++) {
                    lines.x.push(crispUp(plot.left + (i + 0.5) / grid.columns * plot.width));
                }
                for (i = 1; i < grid.rows; i++) {
                    lines.y.push(crispDown(plot.bottom - i / grid.rows * plot.height));
                }
            }
            return lines;
        }

        /* grid lines; none is drawn over the plot border */
        function drawGrid(svg, model) {
            var plot = model.plot;
            var lines = gridLines(model);
            var left = crispDown(plot.left), right = crispDown(plot.right);
            var top = crispDown(plot.top), bottom = crispDown(plot.bottom);
            var d;
            if (model.xAxis.grid !== false) {
                d = '';
                angular.forEach(lines.x, function (x) {
                    if (Math.abs(x - left) >= 1 && Math.abs(x - right) >= 1) {
                        d += 'M ' + x + ' ' + bottom + ' L ' + x + ' ' + top + ' ';
                    }
                });
                if (d) {
                    svg.appendChild(createEl('path', {
                        d: d, fill: 'none', stroke: STYLE.gridColor, 'stroke-width': 1, 'class': 'ogr-chart-xgrid'
                    }));
                }
            }
            if (model.yAxis.grid !== false) {
                d = '';
                angular.forEach(lines.y, function (y) {
                    if (Math.abs(y - top) >= 1 && Math.abs(y - bottom) >= 1) {
                        d += 'M ' + left + ' ' + y + ' L ' + right + ' ' + y + ' ';
                    }
                });
                if (d) {
                    svg.appendChild(createEl('path', {
                        d: d, fill: 'none', stroke: STYLE.gridColor, 'stroke-width': 1, 'class': 'ogr-chart-ygrid'
                    }));
                }
            }
        }

        function drawArea(svg, model) {
            var plot = model.plot;
            svg.appendChild(createEl('rect', {
                x: plot.left, y: plot.top, width: plot.width, height: plot.height,
                fill: 'transparent', stroke: STYLE.areaBorderColor, 'stroke-width': STYLE.areaBorderWidth,
                'class': 'ogr-chart-area'
            }));
        }

        /* title: centred over the plot, in the band above it */
        function drawTitle(svg, model) {
            if (!model.titleSize) {
                return;
            }
            svg.appendChild(textEl(model.titleText, {
                x: (model.plot.left + model.plot.right) / 2,
                y: STYLE.padding + model.titleSize.height / 2 + STYLE.titleBaselineShift,
                'text-anchor': 'middle', 'class': 'ogr-chart-title'
            }, model.titleFont));
        }

        /* clip path of the plot, used by the series, the shading and the tracker */
        function drawClip(svg, model) {
            var plot = model.plot;
            model.clipId = 'ogr-chart-clip-' + Math.random().toString(36).slice(2, 10);
            model.defs = createEl('defs');
            var clip = createEl('clipPath', {id: model.clipId});
            clip.appendChild(createEl('rect', {x: plot.left, y: plot.top, width: plot.width, height: plot.height}));
            model.defs.appendChild(clip);
            svg.appendChild(model.defs);
        }

        /* each series, followed by its markers (drawn opaque over the line); with animate every series also
           gets its own clip rectangle that drawIn widens from the left */
        function drawSeries(svg, model) {
            if (!model.hasData) {
                return;
            }
            var plot = model.plot;
            var g = createEl('g', {'clip-path': 'url(#' + model.clipId + ')', 'class': 'ogr-chart-series'});
            for (var i = 0; i < model.series.length; i++) {
                var s = model.series[i];
                if (s.points.length === 0 || s.axisOnly || s.hidden) {
                    continue;
                }
                var node = (s.kind === 'band') ? drawBand(model, s) : drawLine(model, s);
                if (!node) {
                    continue;
                }
                s.node = node;
                var holder = node;
                if (model.animate) {
                    var revealId = model.clipId + '-' + i;
                    var reveal = createEl('clipPath', {id: revealId});
                    s.revealRect = createEl('rect', {x: plot.left, y: plot.top, width: plot.width, height: plot.height});
                    reveal.appendChild(s.revealRect);
                    model.defs.appendChild(reveal);
                    holder = createEl('g', {'clip-path': 'url(#' + revealId + ')'});
                    holder.appendChild(node);
                }
                g.appendChild(holder);
                if (s.kind === 'line' && s.marker.show) {
                    s.markerNode = drawMarkers(s);
                    g.appendChild(s.markerNode);
                }
            }
            svg.appendChild(g);
        }

        function drawLine(model, s) {
            var g = createEl('g', {opacity: s.opacity, 'class': 'ogr-chart-line'});
            var d = '';
            var run = [];
            for (var j = 0; j < s.points.length; j++) {
                var p = s.points[j];
                if (p.y === null || isNaN(p.y)) {
                    d += linePath(run, s.spline);
                    run = []; // gap for empty points
                    continue;
                }
                p.px = pointX(model, p);
                p.py = yToPx(model, p.y);
                run.push(p);
            }
            d += linePath(run, s.spline);
            g.appendChild(createEl('path', {
                d: d.trim(), fill: 'none', stroke: s.color, 'stroke-width': s.lineWidth,
                'stroke-dasharray': seriesDash(s), 'stroke-linejoin': 'round'
            }));
            return g;
        }

        /* path through a run of points without gaps: straight segments or, with spline, the natural cubic
           spline written as one cubic Bezier segment per pair of points */
        function linePath(run, spline) {
            if (run.length === 0) {
                return '';
            }
            var m = spline ? splineCurvatures(run) : null;
            var d = ' M ' + run[0].px + ' ' + run[0].py;
            for (var i = 1; i < run.length; i++) {
                var p0 = run[i - 1], p1 = run[i];
                if (!m) {
                    d += ' L ' + p1.px + ' ' + p1.py;
                    continue;
                }
                var h = p1.px - p0.px;
                var slope = (p1.py - p0.py) / h;
                var slope0 = slope - h * (2 * m[i - 1] + m[i]) / 6; // spline slope leaving p0
                var slope1 = slope + h * (m[i - 1] + 2 * m[i]) / 6; // spline slope arriving at p1
                d += ' C ' + (p0.px + h / 3) + ' ' + (p0.py + h * slope0 / 3) +
                    ' ' + (p1.px - h / 3) + ' ' + (p1.py - h * slope1 / 3) +
                    ' ' + p1.px + ' ' + p1.py;
            }
            return d;
        }

        /* second derivatives of the natural cubic spline through the points (zero at both ends), from the
           tridiagonal system of the inner points; null (straight segments) for fewer than three points or
           x positions that do not strictly increase */
        function splineCurvatures(run) {
            var n = run.length;
            if (n < 3) {
                return null;
            }
            var i;
            var h = [];
            for (i = 0; i < n - 1; i++) {
                h[i] = run[i + 1].px - run[i].px;
                if (!(h[i] > 0)) {
                    return null;
                }
            }
            var diag = [], rhs = [];
            for (i = 1; i < n - 1; i++) {
                var b = 2 * (h[i - 1] + h[i]);
                var r = 6 * ((run[i + 1].py - run[i].py) / h[i] - (run[i].py - run[i - 1].py) / h[i - 1]);
                if (i > 1) {
                    var w = h[i - 1] / diag[i - 1];
                    b -= w * h[i - 1];
                    r -= w * rhs[i - 1];
                }
                diag[i] = b;
                rhs[i] = r;
            }
            var m = [];
            m[0] = 0;
            m[n - 1] = 0;
            for (i = n - 2; i >= 1; i--) {
                m[i] = (rhs[i] - h[i] * m[i + 1]) / diag[i];
            }
            return m;
        }

        /* a dot of the series colour on every point with a value */
        function drawMarkers(s) {
            var g = createEl('g', {'class': 'ogr-chart-markers'});
            var r = (s.marker.size || 2) / Math.SQRT2;
            angular.forEach(s.points, function (p) {
                if (p.y !== null && !isNaN(p.y)) {
                    g.appendChild(createEl('circle', {cx: p.px, cy: p.py, r: r, fill: s.color}));
                }
            });
            return g;
        }

        /* band: area between the upper and the lower edge */
        function drawBand(model, s) {
            var upper = [], lower = [];
            for (var j = 0; j < s.points.length; j++) {
                var p = s.points[j];
                if (p.upper === null || p.lower === null || isNaN(p.upper) || isNaN(p.lower)) {
                    continue;
                }
                var x = pointX(model, p);
                p.px = x;
                p.py = yToPx(model, (p.upper + p.lower) / 2);
                upper.push(x + ' ' + yToPx(model, p.upper));
                lower.push(x + ' ' + yToPx(model, p.lower));
            }
            if (upper.length === 0) {
                return null;
            }
            var d = 'M ' + upper[0];
            for (var i = 1; i < upper.length; i++) {
                d += ' L ' + upper[i];
            }
            for (i = lower.length - 1; i >= 0; i--) {
                d += ' L ' + lower[i];
            }
            d += ' Z';
            var g = createEl('g', {opacity: s.opacity, 'class': 'ogr-chart-band'});
            g.appendChild(createEl('path', {
                d: d, fill: s.color, stroke: s.edgeColor || 'transparent', 'stroke-width': 1
            }));
            return g;
        }

        /* axis lines, ticks and labels; a chart whose series have no points keeps its axis lines and X ticks */
        function drawAxes(svg, model) {
            if (!model.axesVisible && !model.bareAxes) {
                return;
            }
            var plot = model.plot;
            var lines = gridLines(model);
            var left = crispDown(plot.left), right = crispDown(plot.right);
            var top = crispDown(plot.top), bottom = crispDown(plot.bottom);
            var i;

            // X axis labels, line and ticks
            var gx = createEl('g', {'class': 'ogr-chart-xaxis'});
            if (model.axesVisible) {
                angular.forEach(model.xTicks, function (tick) {
                    if (!tick.hidden && tick.text !== '') {
                        gx.appendChild(textEl(tick.text, {
                            x: tick.label.x, y: tick.label.y, 'text-anchor': tick.label.anchor
                        }, model.xFont));
                    }
                });
            }
            gx.appendChild(createEl('line', {
                x1: left, x2: right, y1: bottom, y2: bottom, stroke: STYLE.axisColor, 'stroke-width': 1
            }));
            var ticksD = '';
            angular.forEach(lines.x, function (x) {
                ticksD += 'M ' + x + ' ' + (bottom + STYLE.xTickSize) + ' L ' + x + ' ' + bottom + ' ';
            });
            gx.appendChild(createEl('path', {d: ticksD, fill: 'none', stroke: STYLE.axisColor, 'stroke-width': 1}));
            svg.appendChild(gx);

            // Y axis labels, line, ticks and title
            var gy = createEl('g', {'class': 'ogr-chart-yaxis'});
            ticksD = '';
            for (i = 0; i < model.yTicks.length; i++) {
                var tick = model.yTicks[i];
                ticksD += 'M ' + (left - STYLE.yTickSize) + ' ' + lines.y[i] + ' L ' + left + ' ' + lines.y[i] + ' ';
                // the label's middle at the value: baseline a quarter of the text height below it
                gy.appendChild(textEl(tick.text, {
                    x: plot.left - 10.5, y: yToPx(model, tick.value) + measureDom(tick.text, model.yFont).height / 4,
                    'text-anchor': 'end'
                }, model.yFont));
            }
            gy.appendChild(createEl('line', {
                x1: left, x2: left, y1: top, y2: bottom, stroke: STYLE.axisColor, 'stroke-width': 1
            }));
            gy.appendChild(createEl('path', {d: ticksD, fill: 'none', stroke: STYLE.axisColor, 'stroke-width': 1}));
            if (model.yTitleVisible) {
                var tx = STYLE.padding + 7;
                var ty = plot.top + plot.height / 2;
                gy.appendChild(textEl(model.yAxis.title.text, {
                    x: tx, y: ty, 'text-anchor': 'middle', 'dominant-baseline': 'middle',
                    transform: 'rotate(-90,' + tx + ',' + ty + ')'
                }, fontOf(model.yAxis.title.font || model.yAxis.font)));
            }
            svg.appendChild(gy);
        }

        //************************************************************************************
        // animate: every line is revealed from the left over drawInMs (eased in and out) while its markers
        // grow to full size one after another, spread evenly over the same time
        //************************************************************************************

        function drawIn(model) {
            var start = Date.now();
            var end = STYLE.drawInMs;
            var items = [];
            angular.forEach(model.series, function (s) {
                if (!s.revealRect) {
                    return;
                }
                s.revealRect.setAttribute('width', 0);
                var markers = [];
                var dots = s.markerNode ? s.markerNode.childNodes : [];
                for (var i = 0; i < dots.length; i++) {
                    var delay = Math.floor(i * STYLE.drawInMs / dots.length);
                    markers.push({node: dots[i], delay: delay, x: dots[i].getAttribute('cx'), y: dots[i].getAttribute('cy')});
                    dots[i].setAttribute('transform', 'scale(0)');
                    end = Math.max(end, delay + STYLE.markerPopMs);
                }
                items.push({rect: s.revealRect, markers: markers});
            });
            var frame = requestFrame(step);
            return function stop() {
                cancelFrame(frame);
                frame = null;
            };

            function step() {
                var t = Date.now() - start;
                angular.forEach(items, function (item) {
                    item.rect.setAttribute('width', model.plot.width * easeInOut(Math.min(1, t / STYLE.drawInMs)));
                    angular.forEach(item.markers, function (m) {
                        if (t >= m.delay && !m.done) {
                            var grown = Math.min(1, (t - m.delay) / STYLE.markerPopMs);
                            m.node.setAttribute('transform', 'translate(' + m.x + ' ' + m.y + ') scale(' +
                                easeInOut(grown) + ') translate(' + -m.x + ' ' + -m.y + ')');
                            m.done = grown >= 1;
                        }
                    });
                });
                frame = t < end ? requestFrame(step) : null;
            }
        }

        /* 0 -> 1, slow at both ends (half a cosine wave) */
        function easeInOut(p) {
            return (1 - Math.cos(p * Math.PI)) / 2;
        }

        /* legend layout, below the plot: every item takes a slot of symbol + gap + name + item padding; slots
           are filled into rows no wider than the chart minus a side margin, and the widest row is centred */
        function layoutLegend(model) {
            var items = model.series.filter(function (s) {
                return s.points.length > 0;
            });
            if (items.length === 0) {
                return null;
            }
            var pad = STYLE.legendItemPadding;
            var maxRowWidth = model.width - 2 * STYLE.legendSideMargin;
            var rows = [];
            var row = null;
            var itemHeight = STYLE.legendItemSize;
            angular.forEach(items, function (series) {
                var size = measureDom(series.label, model.legendFont);
                var slot = STYLE.legendItemSize + STYLE.legendElementSpacing + size.width + pad;
                itemHeight = Math.max(itemHeight, size.height);
                if (!row || (row.items.length > 0 && row.width + slot > maxRowWidth)) {
                    row = {items: [], width: 0};
                    rows.push(row);
                }
                row.items.push({series: series, width: slot - pad, textHeight: size.height});
                row.width += slot;
            });
            var widest = Math.max.apply(null, rows.map(function (r) {
                return r.width;
            }));
            return {
                rows: rows,
                itemHeight: itemHeight,
                // the widest row (without its trailing padding) centred, nudged right by half a padding
                left: (model.width - (widest - pad)) / 2 + pad / 2,
                height: rows.length * itemHeight + (rows.length - 1) * pad + 2 * STYLE.legendElementSpacing
            };
        }

        /* legend items: a line series is shown as a small ring on a short line, a band as a filled square,
           followed by the series label */
        function drawLegend(svg, model) {
            var box = model.legendBox;
            if (!model.legendVisible || !box) {
                return;
            }
            var size = STYLE.legendItemSize;
            var space = STYLE.legendElementSpacing;
            var boxTop = model.height - STYLE.legendBottomGap - box.height;
            var g = createEl('g', {'class': 'ogr-chart-legend'});
            model.legendItems = [];
            for (var r = 0; r < box.rows.length; r++) {
                var row = box.rows[r];
                var itemX = box.left;
                var itemY = boxTop + space + r * (box.itemHeight + STYLE.legendItemPadding);
                for (var i = 0; i < row.items.length; i++) {
                    var entry = row.items[i];
                    var s = entry.series;
                    var color = s.hidden ? 'gray' : s.color;
                    var startX = itemX + size / 2;
                    var startY = itemY + Math.max(entry.textHeight, size) / 2;
                    var item = createEl('g', {'class': 'ogr-chart-legend-item', cursor: 'pointer'});
                    if (s.kind === 'band') {
                        item.appendChild(createEl('rect', {
                            x: startX - size / 2, y: startY - size / 2, width: size, height: size,
                            fill: color, stroke: 'transparent'
                        }));
                    } else {
                        item.appendChild(lineSymbol(startX, startY + 1, color, s.marker.show));
                    }
                    var textFont = s.hidden ? angular.extend({}, model.legendFont, {color: 'gray'}) : model.legendFont;
                    item.appendChild(textEl(s.label, {
                        x: itemX + size + space, y: startY + entry.textHeight / 4, 'text-anchor': 'start'
                    }, textFont));
                    g.appendChild(item);
                    model.legendItems.push({node: item, series: s});
                    itemX += entry.width + STYLE.legendItemPadding;
                }
            }
            svg.appendChild(g);
        }

        /* ring centred at (cx, cy) with a line stub on its left and right; without markers just the line */
        function lineSymbol(cx, cy, color, withMarker) {
            var ring = STYLE.legendSymbolRing;
            var reach = STYLE.legendSymbolReach;
            var stroke = {stroke: color, 'stroke-width': STYLE.legendSymbolStroke};
            var symbol = createEl('g', {'class': 'ogr-chart-legend-symbol'});
            if (!withMarker) {
                symbol.appendChild(createEl('line', angular.extend({x1: cx - reach, y1: cy, x2: cx + reach, y2: cy}, stroke)));
                return symbol;
            }
            symbol.appendChild(createEl('line', angular.extend({x1: cx - reach, y1: cy, x2: cx - ring, y2: cy}, stroke)));
            symbol.appendChild(createEl('line', angular.extend({x1: cx + ring, y1: cy, x2: cx + reach, y2: cy}, stroke)));
            symbol.appendChild(createEl('circle', angular.extend({cx: cx, cy: cy, r: ring, fill: 'transparent'}, stroke)));
            return symbol;
        }

        /* placeholder: a text (e.g. "Empty") centred over the plot area, shown in place of a chart */
        function drawPlaceholder(el, model) {
            var message = model.placeholder;
            if (!message.show) {
                return null;
            }
            var overlay = $document[0].createElement('div');
            overlay.className = 'ogr-chart-placeholder';
            overlay.style.position = 'absolute';
            overlay.style.pointerEvents = 'none';
            var svgLeft = Math.max(0, (el.clientWidth - model.width) / 2);
            overlay.style.left = (svgLeft + model.plot.left) + 'px';
            overlay.style.top = model.plot.top + 'px';
            overlay.style.width = model.plot.width + 'px';
            overlay.style.height = model.plot.height + 'px';
            overlay.style.display = 'flex';
            overlay.style.alignItems = 'center';
            overlay.style.justifyContent = 'center';
            var node = $document[0].createElement('div');
            node.textContent = String(message.text || '');
            node.style.fontSize = message.fontSize || '';
            node.style.opacity = angular.isDefined(message.opacity) ? message.opacity : 1;
            overlay.appendChild(node);
            el.appendChild(overlay);
            return overlay;
        }

        //************************************************************************************
        // tracker: vertical guide line under the pointer, a marker on every series and one tooltip per series,
        // stacked so they do not overlap
        //************************************************************************************

        function bindTracker(el, svg, model) {
            var plot = model.plot;
            var group = createEl('g', {'class': 'ogr-chart-hover', 'pointer-events': 'none'});
            svg.appendChild(group);
            var hovered = null;
            var tracks = model.series.map(buildTrack);
            var pending = null;   // latest mouse event, processed once per animation frame
            var frame = null;
            var shownKey = null;  // points currently shown; the drawing only depends on them

            svg.addEventListener('mousemove', onMove);
            svg.addEventListener('mouseleave', onLeave);
            return stop;

            function stop() {
                cancelFrame(frame);
                frame = null;
                pending = null;
            }

            function onLeave() {
                stop();
                clear(group);
                shownKey = null;
                setHovered(null);
            }

            function onMove(evt) {
                pending = {clientX: evt.clientX, target: evt.target};
                if (frame === null) {
                    frame = requestFrame(processMove);
                }
            }

            function processMove() {
                frame = null;
                var evt = pending;
                pending = null;
                if (!evt) {
                    return;
                }
                var rect = svg.getBoundingClientRect();
                var mx = evt.clientX - rect.left;
                setHovered(seriesOfNode(evt.target));
                // track while the pointer is horizontally inside the x axis
                if (!(mx > plot.left && mx < plot.right)) {
                    clear(group);
                    shownKey = null;
                    return;
                }
                showTracker(mx);
            }

            /* seriesStyle.hoverOpacity: the series under the pointer is redrawn with that opacity */
            function seriesOfNode(node) {
                while (node && node !== svg) {
                    for (var i = 0; i < model.series.length; i++) {
                        if (model.series[i].node === node) {
                            return model.series[i];
                        }
                    }
                    node = node.parentNode;
                }
                return null;
            }

            function setHovered(series) {
                if (!angular.isNumber(model.hoverOpacity)) {
                    return;
                }
                if (hovered === series) {
                    return;
                }
                if (hovered && hovered.node) {
                    hovered.node.setAttribute('opacity', hovered.opacity);
                }
                hovered = series;
                if (hovered && hovered.node) {
                    hovered.node.setAttribute('opacity', model.hoverOpacity);
                }
            }

            function showTracker(mx) {
                // pixel -> axis value
                var xValue = model.xMin + (mx - plot.left) / plot.width * (model.xMax - model.xMin);
                var hits = [];
                var key = '';
                var i;
                // one x for the whole chart: the tracked point nearest to the pointer over all series
                // (on a tie the earlier series wins)
                var target = null;
                for (i = 0; i < tracks.length; i++) {
                    var near = closestPointX(tracks[i], xValue);
                    if (near && (target === null || Math.abs(near.xValue - xValue) < Math.abs(target - xValue))) {
                        target = near.xValue;
                    }
                }
                // only series with a point at exactly that x take part; the x must be inside the x range and
                // the value not below the y axis minimum
                if (target !== null && target >= model.xMin && target <= model.xMax) {
                    for (i = 0; i < model.series.length; i++) {
                        var index = pointIndexAt(tracks[i], target);
                        if (index === null) {
                            continue;
                        }
                        var series = model.series[i];
                        var point = series.points[index];
                        var yValue = (series.kind === 'band') ? point.upper : point.y;
                        if (yValue < model.yMin) {
                            continue;
                        }
                        hits.push({series: series, index: index, xValue: target, yValue: yValue});
                        key += i + ':' + index + ',';
                    }
                }
                if (key === shownKey) {
                    return; // same points as the last frame: identical drawing, nothing to redo
                }
                shownKey = key;
                clear(group);

                var tips = [];
                var widestBox = 0;
                var trackerX = null;
                var pad = TRACKER.textMargin;
                for (i = 0; i < hits.length; i++) {
                    var h = hits[i];
                    var text = tooltipText(model, h.series, h.index, defaultTooltipText(h.series, h.index));
                    var font = tooltipFont(h.series);
                    var textSize = measureDom(text, font);
                    var tip = {
                        x: xToPx(model, h.xValue),
                        anchor: yToPx(model, h.yValue),
                        series: h.series,
                        text: text,
                        font: font,
                        textHeight: textSize.height,
                        width: textSize.width + 2 * pad,
                        height: textSize.height + 2 * pad
                    };
                    tips.push(tip);
                    trackerX = tip.x;
                    widestBox = Math.max(widestBox, tip.width);
                }
                if (tips.length === 0) {
                    return;
                }

                // tracker line at the shared x, clipped to the plot
                group.appendChild(createEl('path', {
                    d: 'M ' + trackerX + ' ' + plot.top + ' L ' + trackerX + ' ' + plot.bottom,
                    fill: 'none', stroke: TRACKER.guideColor, 'stroke-width': TRACKER.guideWidth,
                    'clip-path': 'url(#' + model.clipId + ')', 'class': 'ogr-chart-tracker'
                }));
                // a marker on every tracked point
                var symbols = createEl('g', {'clip-path': 'url(#' + model.clipId + ')', 'class': 'ogr-chart-tracker-markers'});
                var radius = TRACKER.markerSize / Math.SQRT2;
                for (i = 0; i < tips.length; i++) {
                    symbols.appendChild(createEl('circle', {
                        cx: tips[i].x, cy: tips[i].anchor, r: radius,
                        fill: TRACKER.markerFill, 'stroke-width': TRACKER.markerRingWidth,
                        stroke: tips[i].series.color, opacity: 1
                    }));
                }
                group.appendChild(symbols);

                tips.sort(function (a, b) {
                    return a.anchor - b.anchor;
                });
                stackTooltips(tips);
                // a box goes right of its point while that side still has room for sideRoom times the widest box
                for (i = 0; i < tips.length; i++) {
                    var fitsRight = tips[i].x + TRACKER.pointGap + TRACKER.sideRoom * widestBox <= plot.right;
                    drawTooltip(tips[i], fitsRight ? 'right' : 'left');
                }
            }

            /* per series lookup table, built once per render: distinct x values in ascending order,
               each with the first point index that has it. Hidden / axis-only series are not tracked, and
               neither are points without a value (gaps) */
            function buildTrack(series) {
                if (series.axisOnly || series.hidden || series.points.length === 0) {
                    return null;
                }
                var rows = [];
                for (var j = 0; j < series.points.length; j++) {
                    var p = series.points[j];
                    var x = model.isCategory ? p.xIndex : parseFloat(p.x);
                    if (isNaN(x)) {
                        return null; // a series with a non-numeric x value is never tracked
                    }
                    var hasValue = (series.kind === 'band')
                        ? isFiniteNumber(p.upper) && isFiniteNumber(p.lower)
                        : isFiniteNumber(p.y);
                    if (hasValue) {
                        rows.push({x: x, index: j});
                    }
                }
                if (rows.length === 0) {
                    return null;
                }
                rows.sort(function (a, b) {
                    return a.x - b.x || a.index - b.index;
                });
                var xs = [], firstIndex = [];
                for (j = 0; j < rows.length; j++) {
                    if (j === 0 || rows[j].x !== rows[j - 1].x) {
                        xs.push(rows[j].x);
                        firstIndex.push(rows[j].index);
                    }
                }
                return {xs: xs, firstIndex: firstIndex};
            }

            /* the series point whose x value is nearest to the pointer, only while the pointer is within half a
               unit of the series' own x extent; on a tie the value that occurs earlier in the series wins */
            function closestPointX(track, xValue) {
                if (!track) {
                    return null;
                }
                var xs = track.xs;
                if (!(xValue >= xs[0] - 0.5 && xValue <= xs[xs.length - 1] + 0.5)) {
                    return null;
                }
                var lo = 0, hi = xs.length;
                while (lo < hi) {
                    var mid = (lo + hi) >> 1;
                    if (xs[mid] < xValue) {
                        lo = mid + 1;
                    } else {
                        hi = mid;
                    }
                }
                var best = -1;
                for (var c = lo - 1; c <= lo; c++) {
                    if (c < 0 || c >= xs.length) {
                        continue;
                    }
                    if (best < 0) {
                        best = c;
                        continue;
                    }
                    var dc = Math.abs(xs[c] - xValue), db = Math.abs(xs[best] - xValue);
                    if (dc < db || (dc === db && track.firstIndex[c] < track.firstIndex[best])) {
                        best = c;
                    }
                }
                return {index: track.firstIndex[best], xValue: xs[best]};
            }

            /* index of the series point at exactly this x value, or null when the series has none */
            function pointIndexAt(track, x) {
                if (!track) {
                    return null;
                }
                var lo = 0, hi = track.xs.length - 1;
                while (lo <= hi) {
                    var mid = (lo + hi) >> 1;
                    if (track.xs[mid] === x) {
                        return track.firstIndex[mid];
                    }
                    if (track.xs[mid] < x) {
                        lo = mid + 1;
                    } else {
                        hi = mid - 1;
                    }
                }
                return null;
            }

            /* default tooltip text: "x : y" for lines, "x <br/> High : upper <br/> Low : lower" for bands;
               x is the axis label of the point (category text or the raw number), the values are raw */
            function defaultTooltipText(series, index) {
                var point = series.points[index];
                var x = model.isCategory ? model.categories[point.xIndex] : point.x;
                if (series.kind === 'band') {
                    return x + ' <br/> High : ' + point.upper + ' <br/> Low : ' + point.lower;
                }
                return x + ' : ' + point.y;
            }

            /* tooltip font: defaults overridden by series.tooltipFont */
            function tooltipFont(series) {
                var f = series.tooltipFont;
                return {
                    size: f.size || TRACKER.font.size,
                    fontFamily: f.fontFamily || TRACKER.font.fontFamily,
                    fontStyle: f.fontStyle || TRACKER.font.fontStyle,
                    fontWeight: f.fontWeight || TRACKER.font.fontWeight,
                    color: f.color || TRACKER.textColor
                };
            }

            /* spec.tooltipText(point, defaultText) may replace the default text; it gets the point as given in
               the spec ({x, y, text} or {x, upper, lower, text}) */
            function tooltipText(chartModel, series, index, defaultText) {
                if (!angular.isFunction(chartModel.tooltipText)) {
                    return defaultText;
                }
                var p = series.points[index];
                var point = (series.kind === 'band')
                    ? {x: p.x, upper: p.upper, lower: p.lower, text: p.text}
                    : {x: p.x, y: p.y, text: p.text};
                var text = chartModel.tooltipText(point, defaultText);
                return (text === null || angular.isUndefined(text)) ? defaultText : String(text);
            }

            /* stacking (tips sorted by their point's y): every box starts centred on its point. Boxes that
               would touch form a column - boxes one spacing apart, centred halfway between the column's first
               and last point - and a column that then reaches the one above absorbs it. If the whole stack fits
               into the plot it is pushed inside: first off the bottom edge, then off the top edge; otherwise it
               is laid out from the plot top down. */
            function stackTooltips(tips) {
                var gap = TRACKER.stackGap;
                var columns = [];
                angular.forEach(tips, function (tip) {
                    var column = centred({tips: [tip], from: tip.anchor, to: tip.anchor, height: tip.height});
                    var above = columns[columns.length - 1];
                    while (above && above.top + above.height + gap > column.top) {
                        columns.pop();
                        column = centred({
                            tips: above.tips.concat(column.tips),
                            from: above.from,
                            to: column.to,
                            height: above.height + gap + column.height
                        });
                        above = columns[columns.length - 1];
                    }
                    columns.push(column);
                });
                angular.forEach(columns, function (column) {
                    var y = column.top;
                    angular.forEach(column.tips, function (tip) {
                        tip.top = y;
                        y += tip.height + gap;
                    });
                });

                var needed = gap * (tips.length - 1);
                angular.forEach(tips, function (tip) {
                    needed += tip.height;
                });
                var i;
                var limit;
                if (needed > plot.height - TRACKER.clearanceTop - TRACKER.clearanceBottom) {
                    // too many to fit: the stack starts at the plot top, the boxes running past the bottom
                    // are left out when drawing
                    limit = plot.top + TRACKER.clearanceTop;
                    for (i = 0; i < tips.length; i++) {
                        tips[i].top = limit;
                        limit += tips[i].height + gap;
                    }
                    return;
                }
                limit = plot.bottom - TRACKER.clearanceBottom;
                for (i = tips.length - 1; i >= 0; i--) {
                    tips[i].top = Math.min(tips[i].top, limit - tips[i].height);
                    limit = tips[i].top - gap;
                }
                limit = plot.top + TRACKER.clearanceTop;
                for (i = 0; i < tips.length; i++) {
                    tips[i].top = Math.max(tips[i].top, limit);
                    limit = tips[i].top + tips[i].height + gap;
                }
            }

            function centred(column) {
                column.top = (column.from + column.to - column.height) / 2;
                return column;
            }

            /* one tooltip: a box beside its point with an arrow pointing at it, in the series colour; left out
               when the box or the point is outside the plot */
            function drawTooltip(tip, side) {
                var inside = function (x, y) {
                    return x >= plot.left && x <= plot.right && y >= plot.top && y <= plot.bottom;
                };
                if (!inside(tip.x, tip.anchor) || tip.top < plot.top || tip.top + tip.height > plot.bottom) {
                    return;
                }
                var box = {
                    x: side === 'right' ? tip.x + TRACKER.pointGap : tip.x - TRACKER.pointGap - tip.width,
                    y: tip.top,
                    width: tip.width,
                    height: tip.height
                };
                var node = createEl('g', {'class': 'ogr-chart-tooltip'});
                node.appendChild(createEl('path', {
                    d: calloutPath(box, side === 'right' ? 'left' : 'right', tip.anchor),
                    fill: tip.series.color, stroke: 'transparent', 'stroke-width': 0,
                    'fill-opacity': TRACKER.boxOpacity
                }));

                // lines share the measured text height; each baseline sits so the text looks centred in its line
                var lines = tip.text.split('<br/>');
                var lineHeight = tip.textHeight / lines.length;
                var textX = box.x + TRACKER.textMargin;
                var baseline = box.y + TRACKER.textMargin + lineHeight / 2 + (parseFloat(tip.font.size) || 12) / 3;
                var label = createEl('text', {
                    x: textX, y: baseline, fill: tip.font.color, 'font-size': tip.font.size,
                    'font-family': tip.font.fontFamily, 'font-style': tip.font.fontStyle,
                    'font-weight': tip.font.fontWeight, 'text-anchor': 'start'
                });
                if (lines.length === 1) {
                    label.textContent = tip.text;
                } else {
                    angular.forEach(lines, function (line, k) {
                        var span = createEl('tspan', {x: textX, dy: k === 0 ? 0 : lineHeight});
                        span.textContent = line;
                        label.appendChild(span);
                    });
                }
                node.appendChild(label);
                group.appendChild(node);
            }
        }

        /* outline of a tooltip box: rounded corners (circular arcs) and an arrow on arrowSide whose base is
           centred on that side and whose tip is at the point's height (the box may be shifted by stacking, the
           tip never is). Drawn clockwise from the top left corner. */
        function calloutPath(box, arrowSide, tipY) {
            var r = TRACKER.boxRadius;
            var arrow = TRACKER.arrowSize;
            var left = box.x, right = box.x + box.width;
            var top = box.y, bottom = box.y + box.height;
            var middle = top + box.height / 2;
            var arc = function (x, y) {
                return ['A', r, r, 0, 0, 1, x, y];
            };
            var d = ['M', left + r, top, 'H', right - r].concat(arc(right, top + r));
            if (arrowSide === 'right') {
                d.push('V', middle - arrow / 2, 'L', right + arrow, tipY, 'L', right, middle + arrow / 2);
            }
            d.push('V', bottom - r);
            d = d.concat(arc(right - r, bottom), ['H', left + r], arc(left, bottom - r));
            if (arrowSide === 'left') {
                d.push('V', middle + arrow / 2, 'L', left - arrow, tipY, 'L', left, middle - arrow / 2);
            }
            d.push('V', top + r);
            d = d.concat(arc(left + r, top), ['Z']);
            return d.join(' ');
        }

        /* size of the text as laid out by the browser in a hidden element (integer offsetWidth/offsetHeight);
           used for every label, legend and tooltip measurement */
        function measureDom(text, font) {
            var cacheKey = font.size + '|' + font.fontFamily + '|' + font.fontStyle + '|' + font.fontWeight + '|' + text;
            if (measureCache.hasOwnProperty(cacheKey)) {
                return measureCache[cacheKey];
            }
            if (!measureNode) {
                measureNode = $document[0].createElement('text');
                measureNode.id = 'ogrChartMeasureText';
                measureNode.style.position = 'absolute';
                measureNode.style.top = '-100px';
                measureNode.style.left = '0';
                measureNode.style.visibility = 'hidden';
                measureNode.style.whiteSpace = 'nowrap';
                measureNode.style.backgroundColor = 'white';
                $document[0].body.appendChild(measureNode);
            }
            measureNode.style.display = 'block';
            // the text is never parsed as markup: "<br/>" separators become real line breaks,
            // everything else is plain text
            while (measureNode.firstChild) {
                measureNode.removeChild(measureNode.firstChild);
            }
            var lines = String(text).split('<br/>');
            for (var i = 0; i < lines.length; i++) {
                if (i > 0) {
                    measureNode.appendChild($document[0].createElement('br'));
                }
                measureNode.appendChild($document[0].createTextNode(lines[i]));
            }
            // reset first: an invalid or missing value would otherwise keep the previous measurement's setting
            measureNode.style.fontStyle = '';
            measureNode.style.fontWeight = '';
            measureNode.style.fontSize = font.size;
            measureNode.style.fontStyle = font.fontStyle || '';
            measureNode.style.fontFamily = font.fontFamily;
            measureNode.style.fontWeight = font.fontWeight || '';
            var size = {width: measureNode.offsetWidth, height: measureNode.offsetHeight};
            measureNode.style.display = 'none';
            if (measureCacheSize >= 5000) {
                measureCache = {};
                measureCacheSize = 0;
            }
            measureCache[cacheKey] = size;
            measureCacheSize++;
            return size;
        }

        function requestFrame(fn) {
            return $window.requestAnimationFrame ? $window.requestAnimationFrame(fn) : $window.setTimeout(fn, 16);
        }

        function cancelFrame(id) {
            if (id === null) {
                return;
            }
            if ($window.cancelAnimationFrame) {
                $window.cancelAnimationFrame(id);
            } else {
                $window.clearTimeout(id);
            }
        }

        function clear(node) {
            while (node.firstChild) {
                node.removeChild(node.firstChild);
            }
        }
    }
})();
